import React, {Fragment, PropsWithChildren} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {FeaturesEnum, HomeEnum, ServicesEnum} from 'presentation/constants';
import {useContractStatus, useHostName} from 'presentation/hooks';
import {useAccountInformation, useMediaQueries} from 'presentation/pages';
import {translations} from 'presentation/translations-new';
import {ProductLogo} from '../ProductLogo';

import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {SearchBar} from '../SearchBar';
import {ProfileMenu} from './ProfileMenu';
import {RoutesHandler} from './RoutesHandler';
import {SubRouteHandler} from './SubRouteHandler';
import {TokenButton} from './TokenButton';
import {StyledAppBar, StyledMenuContainer, StyledRouteContainer, StyledToolbar} from './styles';
import {MainHeaderProps} from './types';
import {themeRoutesMapping} from 'presentation/constants/themeRouteMapping';
import {NotificationsButton} from './NotificationsButton';
import {Box} from '@mui/material';

const {searchService} = translations['pt-br'].privateFlow.services;
export const defaultMainHeaderId = 'main-header';

export function MainHeader({
	variant = 'route',
	title,
	handleBack,
	handleClose,
	hideClose,
	hideBackBtn,
	hideProfileMenu,
	children,
	dataTagId,
	breadcrumbs,
	id,
}: PropsWithChildren<MainHeaderProps>) {
	const {isGndiSaoPaulo} = useHostName();
	const {isMobile} = useMediaQueries();
	const {isValid} = useContractStatus();
	const {isResponsibleWithoutBenefits, isB2BAccess, isEarlyAccess} = useAccountInformation();
	const {pathname} = useLocation();

	const shouldShowBackgroundAndLogo = themeRoutesMapping.includes(pathname);
	const isHomePage = pathname === HomeEnum.HOME_PAGE;

	const {isEnabled: isNewProfileFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.ADD__PORTAL_FEATURE_NEW_PROFILE,
	);
	const {isEnabled: isNotificationsFeatureFlagEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_NOTIFICATIONS,
	);

	const showTokenButton =
		isGndiSaoPaulo && !isResponsibleWithoutBenefits && !isNewProfileFeatureFlagEnabled;
	const showNotificationsButton =
		isNotificationsFeatureFlagEnabled && isValid && !isB2BAccess && !isEarlyAccess;

	const Component = () => {
		switch (variant) {
			case 'maintenance':
				return (
					<StyledRouteContainer>
						<ProductLogo customHeight={isGndiSaoPaulo ? 40 : 26} isMobile={isMobile} />
					</StyledRouteContainer>
				);
			case 'logo':
				return (
					<StyledRouteContainer>
						<ProductLogo
							invertColor
							customHeight={isGndiSaoPaulo ? 40 : 26}
							isMobile={isMobile}
						/>
						<Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
							{showTokenButton ? <TokenButton /> : null}
							{showNotificationsButton ? <NotificationsButton /> : null}
							<ProfileMenu />
						</Box>
					</StyledRouteContainer>
				);
			case 'route':
				return (
					<RoutesHandler
						title={title}
						handleBack={handleBack}
						hideProfileMenu={hideProfileMenu}
						breadcrumbs={breadcrumbs}
						dataTagId={dataTagId}
					/>
				);
			case 'subRoute':
				return (
					<SubRouteHandler
						title={title}
						handleBack={handleBack}
						handleClose={handleClose}
						hideBackBtn={hideBackBtn}
						hideClose={hideClose}
						dataTagId={dataTagId}
					/>
				);
			case 'search':
				return (
					<StyledRouteContainer>
						{isValid && !isResponsibleWithoutBenefits && !isB2BAccess ? (
							<Fragment>
								<Link to={ServicesEnum.SERVICES} style={{flex: 1}}>
									<SearchBar placeholder={searchService} readOnly />
								</Link>
								{isGndiSaoPaulo && !isNewProfileFeatureFlagEnabled ? (
									<TokenButton />
								) : null}
							</Fragment>
						) : null}
						<Box sx={{display: 'flex', alignItems: 'center', gap: 2, ml: 'auto'}}>
							{showNotificationsButton ? <NotificationsButton /> : null}
							<StyledMenuContainer
								flex={
									!isValid || isResponsibleWithoutBenefits || isB2BAccess
										? 1
										: undefined
								}>
								<ProfileMenu />
							</StyledMenuContainer>
						</Box>
					</StyledRouteContainer>
				);
			case 'noActions':
				return <SubRouteHandler title={title} hideBackBtn hideClose />;
			default:
				return <RoutesHandler title={title} handleBack={handleBack} />;
		}
	};

	return (
		<StyledAppBar
			id={id || defaultMainHeaderId}
			position="sticky"
			elevation={0}
			$shouldShowBackgroundAndLogo={shouldShowBackgroundAndLogo}
			$isHomePage={isHomePage}
			$variant={variant}>
			<StyledToolbar $showLogo={variant === 'logo'} $isBreadcrumb={!!breadcrumbs}>
				<Component />
			</StyledToolbar>
			{children}
		</StyledAppBar>
	);
}
